import type { AppointmentItem } from '../appointments/appointments-model'

export type TodayStatusKey = AppointmentItem['status']

export type TodayStatusCount = {
  key: TodayStatusKey
  label: string
  count: number
}

export type WorkloadBucket = {
  hour: number
  label: string
  count: number
}

export type ProviderWorkload = {
  name: string
  appointmentCount: number
  shareOfToday: number
  nextStartAtMs: number | null
}

export type ObservedScheduleRange = {
  startAtMs: number
  endAtMs: number
}

export type WorkloadNowState = {
  phase: 'before' | 'during' | 'after'
  position: number
}

const statusLabels: Record<TodayStatusKey, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  unknown: 'Other',
}

function hourInTimeZone(timestamp: number, timeZone: string): number {
  const hour = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(timestamp)

  return Number(hour.find((part) => part.type === 'hour')?.value ?? 0) % 24
}

function hourLabel(hour: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    timeZone: 'UTC',
  }).format(Date.UTC(2020, 0, 1, hour))
}

function activeScheduledAppointments(appointments: AppointmentItem[]) {
  return appointments.filter(
    (appointment): appointment is AppointmentItem & { scheduledAtMs: number } =>
      appointment.scheduledAtMs !== null && appointment.status !== 'cancelled'
  )
}

function activeScheduledTimestamps(appointments: AppointmentItem[]): number[] {
  return activeScheduledAppointments(appointments).map(
    (item) => item.scheduledAtMs
  )
}

export function observedScheduleRange(
  appointments: AppointmentItem[]
): ObservedScheduleRange | null {
  const timestamps = activeScheduledTimestamps(appointments)
  if (timestamps.length === 0) return null
  return {
    startAtMs: Math.min(...timestamps),
    endAtMs: Math.max(...timestamps),
  }
}

export function workloadNowState(
  range: ObservedScheduleRange,
  nowMs: number
): WorkloadNowState {
  if (nowMs < range.startAtMs) return { phase: 'before', position: 0 }
  if (nowMs > range.endAtMs) return { phase: 'after', position: 1 }
  if (range.startAtMs === range.endAtMs) {
    return { phase: 'during', position: 0.5 }
  }
  return {
    phase: 'during',
    position: (nowMs - range.startAtMs) / (range.endAtMs - range.startAtMs),
  }
}

export function appointmentStatusCounts(
  appointments: AppointmentItem[]
): TodayStatusCount[] {
  const counts = new Map<TodayStatusKey, number>([
    ['scheduled', 0],
    ['confirmed', 0],
    ['cancelled', 0],
    ['unknown', 0],
  ])

  appointments.forEach((appointment) => {
    counts.set(appointment.status, (counts.get(appointment.status) ?? 0) + 1)
  })

  return (['confirmed', 'scheduled', 'cancelled', 'unknown'] as const).map(
    (key) => ({ key, label: statusLabels[key], count: counts.get(key) ?? 0 })
  )
}

export function appointmentWorkloadBuckets(
  appointments: AppointmentItem[],
  timeZone: string
): WorkloadBucket[] {
  return appointmentWorkloadGroups(appointments, timeZone).map(
    ({ hour, label, count }) => ({ hour, label, count })
  )
}

export type WorkloadGroup = WorkloadBucket & {
  appointments: (AppointmentItem & { scheduledAtMs: number })[]
}

// One grouping owns both the plotted count and its operational detail list.
export function appointmentWorkloadGroups(
  appointments: AppointmentItem[],
  timeZone: string
): WorkloadGroup[] {
  const grouped = new Map<number, WorkloadGroup['appointments']>()
  activeScheduledAppointments(appointments).forEach((appointment) => {
    const hour = hourInTimeZone(appointment.scheduledAtMs, timeZone)
    const entries = grouped.get(hour) ?? []
    entries.push(appointment)
    grouped.set(hour, entries)
  })
  const hours = [...grouped.keys()]
  if (hours.length === 0) return []
  const firstHour = Math.min(...hours)
  const lastHour = Math.max(...hours)
  return Array.from({ length: lastHour - firstHour + 1 }, (_, index) => {
    const hour = firstHour + index
    const entries = (grouped.get(hour) ?? []).sort(
      (left, right) => left.scheduledAtMs - right.scheduledAtMs
    )
    return {
      hour,
      label: hourLabel(hour),
      count: entries.length,
      appointments: entries,
    }
  })
}

export function providerWorkloads(
  appointments: AppointmentItem[],
  nowMs: number
): ProviderWorkload[] {
  const grouped = new Map<
    string,
    { appointmentCount: number; futureStarts: number[] }
  >()

  appointments.forEach((appointment) => {
    if (appointment.status === 'cancelled') return
    const name = appointment.providerName ?? 'Unassigned'
    const current = grouped.get(name) ?? {
      appointmentCount: 0,
      futureStarts: [],
    }
    current.appointmentCount += 1
    if (
      appointment.scheduledAtMs !== null &&
      appointment.scheduledAtMs >= nowMs
    ) {
      current.futureStarts.push(appointment.scheduledAtMs)
    }
    grouped.set(name, current)
  })

  const total = Array.from(grouped.values()).reduce(
    (sum, entry) => sum + entry.appointmentCount,
    0
  )

  return Array.from(grouped, ([name, value]) => ({
    name,
    appointmentCount: value.appointmentCount,
    shareOfToday: total === 0 ? 0 : value.appointmentCount / total,
    nextStartAtMs:
      value.futureStarts.length > 0 ? Math.min(...value.futureStarts) : null,
  })).sort(
    (left, right) =>
      right.appointmentCount - left.appointmentCount ||
      left.name.localeCompare(right.name)
  )
}
